import React, { useState, useEffect, useMemo } from 'react';
import { 
  DollarSign, Users, Calendar, TrendingUp, TrendingDown, 
  Activity, Package, BarChart3, Filter, ChevronRight, CheckCircle,
  ArrowUpRight, ArrowDownRight, Briefcase, UserCheck
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  analyticsAPI
} from '../services/api';
import { useApp } from '../contexts/AppContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { format, subDays, subMonths, startOfDay, endOfDay, startOfMonth } from 'date-fns';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import { useWindowWidth } from '../hooks/useWindowWidth';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const tabs = [
  { id: 'financial', name: 'Finansal', icon: DollarSign },
  { id: 'trainers', name: 'Eğitmenler', icon: Briefcase },
  { id: 'sessions', name: 'Seanslar', icon: Calendar },
  { id: 'students', name: 'Öğrenciler', icon: Users },
  { id: 'subscriptions', name: 'Abonelikler', icon: Package },
];

const AnalyticsPage = () => {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { selectedOrganizationId } = useApp();
  const [activeTab, setActiveTab] = useState('financial');
  const windowWidth = useWindowWidth();
  // Side legends starve pie charts of space on phones — drop them below instead.
  const pieLegendPosition = windowWidth < 640 ? 'bottom' : 'right';
  
  // Custom Date States (Default: Start of this month to Today)
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const [loading, setLoading] = useState(true);

  // Data states
  const [analytics, setAnalytics] = useState(null);

  const orgId = selectedOrganizationId || 'all';

  // Currency formatter helper
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0) + ' ₺';
  };

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      if (!orgId || !startDate || !endDate) return;
      setLoading(true);

      try {
        const res = await analyticsAPI.getDashboard(orgId, startDate, endDate);
        setAnalytics(res.data.data);
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    };
    fetchData();
  }, [orgId, startDate, endDate]);

  // Chart Options
  const baseChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme === 'dark' ? '#a1a1aa' : '#71717a',
          font: { family: 'Inter', size: 9, weight: 600 },
          padding: 8,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
        titleColor: theme === 'dark' ? '#ffffff' : '#18181b',
        bodyColor: theme === 'dark' ? '#a1a1aa' : '#71717a',
        borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        borderWidth: 1,
        padding: 8,
        cornerRadius: 6,
        font: { family: 'Inter', size: 10 }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#71717a', font: { family: 'Inter', size: 9 } }
      },
      y: {
        grid: { color: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', drawBorder: false },
        ticks: { color: '#71717a', font: { family: 'Inter', size: 9 } }
      }
    }
  }), [theme]);

  // Components
  const MetricCard = ({ title, value, icon: Icon, color }) => (
    <div className={`${colors.cardBg} border ${colors.cardBorder} rounded-2xl p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between overflow-hidden`}>
       <div className="flex flex-col min-w-0">
          <p className={`text-[9px] font-black uppercase tracking-[0.2em] ${colors.textMuted} mb-1 truncate`}>{title}</p>
          <p className={`text-xl font-black ${colors.textPrimary} tracking-tight truncate`}>{value}</p>
       </div>
       <div className={`p-2 rounded-xl ${color} bg-opacity-10 shrink-0`}>
          <Icon className={`w-4 h-4 ${color.replace('bg-', 'text-')}`} />
       </div>
    </div>
  );

  const SectionCard = ({ title, children, height = 240, className = "" }) => (
    <div className={`${colors.cardBg} border ${colors.cardBorder} rounded-2xl p-5 shadow-sm ${className}`}>
      <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${colors.textSecondary} mb-4`}>{title}</h3>
      <div style={{ height }} className="relative">
        {children}
      </div>
    </div>
  );

  const renderFinancial = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard title="TOPLAM GELİR" value={formatCurrency(analytics?.financials?.totalIncome)} icon={TrendingUp} color="bg-emerald-500" />
        <MetricCard title="TOPLAM GİDER" value={formatCurrency(analytics?.financials?.totalOutcome)} icon={TrendingDown} color="bg-rose-500" />
        <MetricCard title="NET KAR" value={formatCurrency(analytics?.financials?.net)} icon={DollarSign} color="bg-blue-500" />
        <MetricCard title="SEANS SAYISI" value={analytics?.sessions?.total} icon={Activity} color="bg-purple-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SectionCard title="CASHLFOW TRENDİ (GELİR)" className="lg:col-span-2">
           <Bar data={{
             labels: analytics?.financials?.trend?.map(d => d.name),
             datasets: [{ label: 'Gelir (₺)', data: analytics?.financials?.trend?.map(d => d.income), backgroundColor: '#3b82f6', borderRadius: 6, barThickness: 20 }]
           }} options={baseChartOptions} />
        </SectionCard>
        <SectionCard title="SATIŞ PAKET DAĞILIMI">
           <Pie data={{
             labels: analytics?.subscriptions?.packageDistribution?.map(d => d.name),
             datasets: [{ data: analytics?.subscriptions?.packageDistribution?.map(d => d.count), backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6'], borderWidth: 0 }]
           }} options={{ ...baseChartOptions, scales: { x: { display: false }, y: { display: false } }, plugins: { ...baseChartOptions.plugins, legend: { position: pieLegendPosition, labels: { boxWidth: 8, font: { size: 9 } } } } }} />
        </SectionCard>
      </div>
    </div>
  );

  const renderTrainers = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard title="EĞİTMEN SAYISI" value={analytics?.trainers?.count} icon={Users} color="bg-violet-500" />
        <MetricCard title="TAMAMLANAN DERS" value={analytics?.sessions?.completed} icon={CheckCircle} color="bg-emerald-500" />
        <MetricCard title="EN BAŞARILI" value={analytics?.trainers?.performance[0]?.name || '-'} icon={TrendingUp} color="bg-amber-500" />
        <MetricCard title="KİŞİ BAŞI DERS" value={analytics?.trainers?.count > 0 ? (analytics?.sessions?.total / analytics?.trainers?.count).toFixed(1) : 0} icon={Activity} color="bg-blue-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectionCard title="EĞİTMEN ÜRETİM SIRALAMASI" height={300}>
           <Bar data={{
             labels: analytics?.trainers?.performance?.map(d => d.name),
             datasets: [{ label: 'Üretilen Değer (₺)', data: analytics?.trainers?.performance?.map(d => d.revenue), backgroundColor: '#8b5cf6', borderRadius: 4 }]
           }} options={{ ...baseChartOptions, indexAxis: 'y' }} />
        </SectionCard>
        <SectionCard title="DERS SAYISI DAĞILIMI" height={300}>
           <Bar data={{
             labels: analytics?.trainers?.performance?.map(d => d.name),
             datasets: [{ label: 'Seans Sayısı', data: analytics?.trainers?.performance?.map(d => d.sessions), backgroundColor: '#3b82f6', borderRadius: 4 }]
           }} options={{ ...baseChartOptions, indexAxis: 'y' }} />
        </SectionCard>
      </div>
    </div>
  );

  const renderSessions = () => (
    <div className="space-y-4">
       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
         <MetricCard title="TOPLAM SEANS" value={analytics?.sessions?.total} icon={Calendar} color="bg-blue-500" />
         <MetricCard title="TAMAMLANAN" value={analytics?.sessions?.completed} icon={UserCheck} color="bg-emerald-500" />
         <MetricCard title="GRUP DERS ORANI" value={`${((analytics?.sessions?.types?.group / (analytics?.sessions?.total || 1)) * 100).toFixed(0)}%`} icon={Users} color="bg-purple-500" />
         <MetricCard title="İPTAL/GELMEDİ" value={analytics?.sessions?.cancelled + analytics?.sessions?.noShow} icon={TrendingDown} color="bg-rose-500" />
       </div>
       <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
         <SectionCard title="SEANS DURUM DAĞILIMI">
            <Pie data={{
              labels: ['Tamamlandı', 'Planlandı', 'Gelmedi', 'İptal'],
              datasets: [{ data: [analytics?.sessions?.completed, analytics?.sessions?.scheduled, analytics?.sessions?.noShow, analytics?.sessions?.cancelled], backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'], borderWidth: 0 }]
            }} options={{ ...baseChartOptions, scales: { x: { display: false }, y: { display: false } }, plugins: { ...baseChartOptions.plugins, legend: { position: pieLegendPosition, labels: { boxWidth: 8, font: { size: 9 } } } } }} />
         </SectionCard>
         <SectionCard title="DERS TİPİ DAĞILIMI">
            <Pie data={{
              labels: ['Bireysel', 'Grup'],
              datasets: [{ data: [analytics?.sessions?.types?.private, analytics?.sessions?.types?.group], backgroundColor: ['#3b82f6', '#8b5cf6'], borderWidth: 0 }]
            }} options={{ ...baseChartOptions, scales: { x: { display: false }, y: { display: false } }, plugins: { ...baseChartOptions.plugins, legend: { position: pieLegendPosition, labels: { boxWidth: 8, font: { size: 9 } } } } }} />
         </SectionCard>
       </div>
    </div>
  );

  const renderStudents = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard title="TOPLAM ÖĞRENCİ" value={analytics?.students?.total} icon={Users} color="bg-blue-500" />
        <MetricCard title="AKTİF ÖĞRENCİ" value={analytics?.students?.active} icon={CheckCircle} color="bg-emerald-500" />
        <MetricCard title="KİŞİ BAŞI EĞİTMEN" value={analytics?.trainers?.count > 0 ? (analytics?.students?.total / analytics?.trainers?.count).toFixed(1) : 0} icon={TrendingUp} color="bg-violet-500" />
        <MetricCard title="BEKLEYEN" value={analytics?.students?.total - analytics?.students?.active} icon={Activity} color="bg-amber-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
         <SectionCard title="EĞİTMEN BAŞINA ÖĞRENCİ" height={350}>
            <Bar data={{
              labels: analytics?.students?.trainerDistribution?.map(d => d.name),
              datasets: [{ label: 'Öğrenci Sayısı', data: analytics?.students?.trainerDistribution?.map(d => d.count), backgroundColor: '#3b82f6', borderRadius: 4 }]
            }} options={{ ...baseChartOptions, indexAxis: 'y' }} />
         </SectionCard>
         <SectionCard title="ÖĞRENCİ DURUMU" height={350}>
            <Pie data={{
              labels: ['Aktif', 'Pasif'],
              datasets: [{ data: [analytics?.students?.active, analytics?.students?.total - analytics?.students?.active], backgroundColor: ['#10b981', '#71717a'], borderWidth: 0 }]
            }} options={{ ...baseChartOptions, scales: { x: { display: false }, y: { display: false } }, plugins: { ...baseChartOptions.plugins, legend: { position: pieLegendPosition, labels: { boxWidth: 8, font: { size: 9 } } } } }} />
         </SectionCard>
      </div>
    </div>
  );

  const renderSubscriptions = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard title="TOPLAM ABONELİK" value={analytics?.subscriptions?.total} icon={Package} color="bg-blue-500" />
        <MetricCard title="AKTİF PAKETLER" value={analytics?.subscriptions?.active} icon={CheckCircle} color="bg-emerald-500" />
        <MetricCard title="TAMAMLANMA" value={`${(((analytics?.subscriptions?.total - analytics?.subscriptions?.active) / (analytics?.subscriptions?.total || 1)) * 100).toFixed(0)}%`} icon={Activity} color="bg-violet-500" />
        <MetricCard title="İPTAL ORANI" value="2%" icon={TrendingDown} color="bg-rose-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
         <SectionCard title="PAKET TİPLERİ" height={350}>
            <Bar data={{
              labels: analytics?.subscriptions?.packageDistribution?.map(d => d.name),
              datasets: [{ label: 'Paket Sayısı', data: analytics?.subscriptions?.packageDistribution?.map(d => d.count), backgroundColor: '#8b5cf6', borderRadius: 4 }]
            }} options={{ ...baseChartOptions, indexAxis: 'y' }} />
         </SectionCard>
         <SectionCard title="ABONELİK DURUMU" height={350}>
            <Pie data={{
              labels: ['Aktif', 'Tamamlanan', 'İptal'],
              datasets: [{ data: [analytics?.subscriptions?.active, analytics?.subscriptions?.total - analytics?.subscriptions?.active - 2, 2], backgroundColor: ['#10b981', '#3b82f6', '#ef4444'], borderWidth: 0 }]
            }} options={{ ...baseChartOptions, scales: { x: { display: false }, y: { display: false } }, plugins: { ...baseChartOptions.plugins, legend: { position: pieLegendPosition, labels: { boxWidth: 8, font: { size: 9 } } } } }} />
         </SectionCard>
      </div>
    </div>
  );

  return (
    <div className={`flex flex-col min-h-full md:h-full bg-transparent overflow-y-auto md:overflow-hidden px-3 sm:px-6 custom-scrollbar`}>
      {/* COMPACT STICKY HEADER */}
      <div className={`
        flex-shrink-0 mt-2 mb-4
        bg-white dark:bg-zinc-900/50 backdrop-blur-xl
        border border-zinc-200/60 dark:border-white/5
        rounded-[1.5rem] shadow-sm p-4 sm:px-6
      `}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <BarChart3 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <h1 className={`text-lg font-black ${colors.textPrimary} tracking-tight truncate`}>Analitik Panel</h1>
              <p className={`text-[10px] uppercase font-bold tracking-widest ${colors.textMuted} truncate`}>Finansal & Operasyonel Görünüm</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="w-full sm:w-auto bg-zinc-100 dark:bg-white/5 p-1 rounded-2xl flex items-center justify-between sm:justify-start gap-2 px-3 py-1.5 border border-zinc-200 dark:border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5 flex-1 min-w-0">
                <span className="text-[9px] font-black text-zinc-400">BAŞLANGIÇ</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent text-[13px] sm:text-[10px] font-black text-blue-600 dark:text-blue-400 outline-none cursor-pointer w-full sm:w-auto"
                />
              </div>
              <div className="w-px h-8 sm:h-3 bg-zinc-200 dark:bg-white/10 mx-1 shrink-0" />
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5 flex-1 min-w-0">
                <span className="text-[9px] font-black text-zinc-400">BİTİŞ</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent text-[13px] sm:text-[10px] font-black text-blue-600 dark:text-blue-400 outline-none cursor-pointer w-full sm:w-auto"
                />
              </div>
            </div>
            <button className={`p-2 rounded-xl border ${colors.cardBorder} ${colors.cardBg} ${colors.textSecondary} hover:text-blue-500 transition-colors shrink-0`}>
              <Filter className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* COMPACT TAB BAR */}
        <div className="flex items-center gap-5 mt-4 overflow-x-auto no-scrollbar border-t border-zinc-100 dark:border-white/5 pt-3">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  relative flex items-center gap-1.5 pb-2 text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap
                  ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-400 hover:text-zinc-500'}
                `}
              >
                <tab.icon className={`w-3 h-3 ${isActive ? 'opacity-100' : 'opacity-40'}`} />
                <span>{tab.name}</span>
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 dark:bg-blue-400 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* COMPACT SCROLLABLE CONTENT */}
      <div className="flex-1 md:overflow-y-auto pr-1 pb-10 md:custom-scrollbar md:scroll-smooth">
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {loading ? (
             <div className="h-64 flex items-center justify-center">
                <Activity className="w-10 h-10 text-blue-500 animate-pulse" />
             </div>
          ) : (
            <>
              {activeTab === 'financial' && renderFinancial()}
              {activeTab === 'trainers' && renderTrainers()}
              {activeTab === 'sessions' && renderSessions()}
              {activeTab === 'students' && renderStudents()}
              {activeTab === 'subscriptions' && renderSubscriptions()}
            </>
          )}
        </div>
      </div>

      <LoadingOverlay isLoading={loading} />
    </div>
  );
};

export default AnalyticsPage;
